import { formatUnits, parseUnits, ZeroAddress } from 'ethers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isSmartAccount } from '@ambire-common/libs/account/account'
import { getIsViewOnly } from '@ambire-common/utils/accounts'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import { TokenResult } from '@ambire-common/libs/portfolio'
import type {
  DcaFlowStatus,
  DcaFlowStep
} from '@web/extension-services/background/controllers/dca/dca'
import { MAX_DCA_SLIPPAGE_BPS } from '@web/extension-services/background/controllers/dca/dcaGuard'
import { getDcaParamsKey } from '@web/extension-services/background/controllers/dca/params'
import { DcaOrderParams, DcaToken } from '@web/extension-services/background/controllers/dca/types'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useControllerState from '@web/hooks/useControllerState'
import useKeystoreControllerState from '@web/hooks/useKeystoreControllerState'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import {
  formatDcaInterval,
  toDateInputValue,
  toTimeInputValue
} from '@web/modules/swap-and-bridge/components/Dca/format'

export type { DcaFlowStatus, DcaFlowStep }

export type DcaIntervalUnit = 'minute' | 'hour' | 'day' | 'week'

export const DCA_UNIT_SECONDS: Record<DcaIntervalUnit, number> = {
  minute: 60,
  hour: 3600,
  day: 86400,
  week: 604800
}

export const DCA_CHAIN_ID = 369

/** The API refuses a `startAt` closer than this; anything earlier means "now". */
const MIN_START_LEAD_MS = 10 * 60 * 1000

const PREVIEW_DEBOUNCE_MS = 500

/** PLS is offered whenever WPLS is: the backend wraps it first. */
const NATIVE_PLS: DcaToken = {
  address: ZeroAddress,
  symbol: 'PLS',
  name: 'Pulse',
  decimals: 18,
  isNative: true
}

const same = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

const getUsdPrice = (token: TokenResult | undefined) =>
  token?.priceIn?.find(({ baseCurrency }) => baseCurrency === 'usd')?.price ?? null

/** Keep digits and one dot, at most `decimals` fraction digits. */
const sanitizeAmount = (text: string, decimals: number) => {
  let value = text.replace(/,/g, '.').replace(/[^0-9.]/g, '')
  if (value.startsWith('.')) value = `0${value}`
  const [int = '', ...rest] = value.split('.')
  const frac = rest.join('').slice(0, decimals)
  const cleanInt = int.replace(/^0+(?=\d)/, '')
  return rest.length ? `${cleanInt}.${frac}` : cleanInt
}

/**
 * Form state + derived values of the DCA tab. The flow itself (prepare →
 * verify → sign each step → report) runs in DcaController in the background;
 * this hook owns what the user types, validates it, keeps the controller's
 * preview in sync (debounced) and translates everything for display.
 */
// formatDecimals('amount') truncates to 2 significant decimals, so a raw
// minimum like 0.19996 would show as "0.19" while 0.19 is still rejected.
// Round the minimum UP to the precision it is shown with.
const ceilToShownDecimals = (value: number) => {
  if (!(value > 0)) return value
  const decimals = value >= 1 ? 2 : -Math.floor(Math.log10(value)) + 1
  const factor = 10 ** decimals
  return Math.ceil(value * factor - 1e-9) / factor
}

const useDca = () => {
  const { t } = useTranslation()
  const { dispatch } = useBackgroundService()
  const state = useControllerState('DcaController')
  const { account, portfolio } = useSelectedAccountControllerState()
  const { keys } = useKeystoreControllerState()
  const { networks } = useNetworksControllerState()

  useEffect(() => {
    dispatch({ type: 'INIT_CONTROLLER_STATE', params: { controller: 'DcaController' } })
    dispatch({ type: 'DCA_CONTROLLER_LOAD_CONFIG', params: {} })
  }, [dispatch])

  const configStatus = state?.configStatus ?? 'idle'
  const config = configStatus === 'ready' ? state.config : null
  const limits = config?.limits ?? null

  const inputTokens = useMemo<DcaToken[]>(() => {
    if (!config) return []
    const list = [...config.inputTokens]
    const wplsIndex = list.findIndex((tk) => same(tk.address, config.wrappedNativeAddress))
    if (wplsIndex !== -1) list.splice(wplsIndex, 0, NATIVE_PLS)
    return list
  }, [config])
  const outputTokens = useMemo<DcaToken[]>(() => config?.outputTokens ?? [], [config])

  // ---- form state ----
  const [tokenInAddr, setTokenInAddr] = useState<string | null>(null)
  const [tokenOutAddr, setTokenOutAddr] = useState<string | null>(null)
  const [amount, setAmountState] = useState('')
  const [intervalValue, setIntervalValueState] = useState('1')
  const [intervalUnit, setIntervalUnit] = useState<DcaIntervalUnit>('day')
  const [executions, setExecutionsState] = useState('2')
  const [start, setStart] = useState(() => ({
    date: toDateInputValue(new Date()),
    time: toTimeInputValue(new Date())
  }))
  const { date: startDate, time: startTime } = start
  // `null` = auto: the backend picks a slippage for the route and the form shows it.
  const [customSlippageBps, setCustomSlippageBps] = useState<number | null>(null)
  // The last auto pick, kept while the next preview loads so the field does not flicker.
  const [autoSlippageBps, setAutoSlippageBps] = useState<number | null>(null)

  const tokenIn = inputTokens.find((tk) => same(tk.address, tokenInAddr)) ?? null
  const tokenOut = outputTokens.find((tk) => same(tk.address, tokenOutAddr)) ?? null
  const sellsNative = !!tokenIn?.isNative

  const availableUnits = useMemo<DcaIntervalUnit[]>(
    () =>
      (Object.keys(DCA_UNIT_SECONDS) as DcaIntervalUnit[]).filter(
        (unit) => !limits || DCA_UNIT_SECONDS[unit] <= limits.maxInterval
      ),
    [limits]
  )

  // Read from the contract via `/dca/config` and changed there at will, but never
  // above the wallet's own ceiling (the guard refuses anything higher).
  const maxSlippageBps = Math.min(config?.maxSlippageBps ?? 0, MAX_DCA_SLIPPAGE_BPS)
  // The API rejects anything under its floor (SLIPPAGE_TOO_LOW); 50 bps only if it omits it.
  const minSlippageBps = Math.min(config?.minSlippageBps ?? 50, maxSlippageBps)
  const slippageBps = Math.max(
    Math.min(
      customSlippageBps ?? autoSlippageBps ?? config?.defaultSlippageBps ?? 100,
      maxSlippageBps
    ),
    minSlippageBps
  )

  // ---- portfolio: balances and prices on PulseChain ----
  const findPortfolioToken = useCallback(
    (address: string | null | undefined) =>
      portfolio?.tokens?.find(
        (tk: TokenResult) =>
          Number(tk.chainId) === DCA_CHAIN_ID &&
          same(tk.address, address) &&
          !tk.flags?.onGasTank &&
          !tk.flags?.rewardsType
      ),
    [portfolio?.tokens]
  )
  const nativePortfolioToken = findPortfolioToken(ZeroAddress)
  const tokenInPortfolio = findPortfolioToken(tokenIn?.address)
  const tokenOutPortfolio = findPortfolioToken(tokenOut?.address)
  // Price proxy fallback: the portfolio only prices tokens the account
  // holds, so a wallet without USDC would otherwise show USDC as $0.
  const proxyUsdPrices = state?.usdPrices
  const proxyPriceOf = useCallback(
    (address: string | null | undefined) =>
      (address && proxyUsdPrices?.[address.toLowerCase()]) || null,
    [proxyUsdPrices]
  )
  const plsUsdPrice =
    getUsdPrice(nativePortfolioToken) ?? proxyPriceOf(config?.wrappedNativeAddress)
  const priceOf = useCallback(
    (address: string | null | undefined, pt: TokenResult | undefined) =>
      getUsdPrice(pt) ??
      (same(address, config?.wrappedNativeAddress) || same(address, ZeroAddress)
        ? plsUsdPrice
        : proxyPriceOf(address)),
    [config?.wrappedNativeAddress, plsUsdPrice, proxyPriceOf]
  )
  const tokenInUsdPrice = priceOf(tokenIn?.address, tokenInPortfolio)
  const tokenOutUsdPrice = priceOf(tokenOut?.address, tokenOutPortfolio)

  // Ask the controller for what the portfolio cannot price. WPLS stands in for
  // PLS, which the network fee is shown in.
  const tokenInPriceMissing = !!tokenIn && getUsdPrice(tokenInPortfolio) === null
  const tokenOutPriceMissing = !!tokenOut && getUsdPrice(tokenOutPortfolio) === null
  const plsPriceMissing = getUsdPrice(nativePortfolioToken) === null
  useEffect(() => {
    const addresses = [
      tokenInPriceMissing && tokenIn?.address,
      tokenOutPriceMissing && tokenOut?.address,
      plsPriceMissing && config?.wrappedNativeAddress
    ].filter((address): address is string => !!address)
    if (!addresses.length) return
    dispatch({ type: 'DCA_CONTROLLER_LOAD_USD_PRICES', params: { addresses } })
  }, [
    dispatch,
    tokenIn?.address,
    tokenOut?.address,
    config?.wrappedNativeAddress,
    tokenInPriceMissing,
    tokenOutPriceMissing,
    plsPriceMissing
  ])
  // No portfolio entry = balance unknown (PulseChain discovery misses tokens),
  // not zero — so no "insufficient balance" and no Max for it.
  const balanceRaw = tokenInPortfolio ? BigInt(tokenInPortfolio.amount ?? 0) : null

  // Both pickers list EVERY token `/dca/config` supports, so a token can be
  // looked up before buying it (Trello #595, #596): the ones the account holds
  // on PulseChain first, highest USD value first, then the rest in config
  // order. A held token the portfolio cannot price sorts after the priced ones
  // but still before the tokens not held.
  const sortByHeldValue = useCallback(
    (tokens: DcaToken[]): DcaToken[] => {
      const ranked = tokens.map((tk) => {
        const pt = findPortfolioToken(tk.address)
        const raw = pt ? BigInt(pt.amount ?? 0) : 0n
        const price = raw > 0n ? priceOf(tk.address, pt) : null
        return {
          tk,
          held: raw > 0n,
          usd: raw > 0n ? Number(formatUnits(raw, tk.decimals)) * (price ?? 0) : 0
        }
      })
      // Array.prototype.sort is stable, so equal values keep the config order.
      return ranked
        .sort((a, b) => Number(b.held) - Number(a.held) || b.usd - a.usd)
        .map(({ tk }) => tk)
    },
    [findPortfolioToken, priceOf]
  )
  const sendTokens = useMemo(() => sortByHeldValue(inputTokens), [sortByHeldValue, inputTokens])
  const receiveTokens = useMemo(
    () => sortByHeldValue(outputTokens),
    [sortByHeldValue, outputTokens]
  )
  /** Input tokens the account holds, most valuable first. */
  const heldInputTokens = useMemo(
    () =>
      sendTokens.filter((tk) => {
        const pt = findPortfolioToken(tk.address)
        return !!pt && BigInt(pt.amount ?? 0) > 0n
      }),
    [sendTokens, findPortfolioToken]
  )

  // Default pair: sell the most valuable token held. Waits for the portfolio
  // instead of pre-selecting a zero-balance token from the config.
  const defaultTokenIn = heldInputTokens[0]?.address ?? null
  useEffect(() => {
    if (!defaultTokenIn || !outputTokens.length) return
    setTokenInAddr((prev) => prev ?? defaultTokenIn)
    setTokenOutAddr(
      (prev) =>
        prev ?? outputTokens.find((tk) => !same(tk.address, defaultTokenIn))?.address ?? null
    )
  }, [defaultTokenIn, outputTokens])

  // ---- parsed values ----
  const amountRaw = useMemo(() => {
    if (!tokenIn || !amount) return null
    try {
      return parseUnits(amount, tokenIn.decimals)
    } catch {
      return null
    }
  }, [amount, tokenIn])

  const intervalNum = Number(intervalValue)
  const intervalSeconds = Math.round(intervalNum * DCA_UNIT_SECONDS[intervalUnit])
  const executionsNum = Number(executions)

  const startMs = Date.parse(`${startDate}T${startTime}`)
  const startsNow = !Number.isFinite(startMs) || startMs < Date.now() + MIN_START_LEAD_MS
  const startAt = startsNow ? undefined : new Date(startMs).toISOString()

  // ---- inline validation (translated) ----
  const intervalError = (() => {
    if (!intervalValue || !(intervalNum > 0)) return t('Enter an interval greater than 0.')
    if (!limits) return null
    if (intervalSeconds < limits.minInterval)
      return t('The interval must be at least {{duration}}.', {
        duration: formatDcaInterval(limits.minInterval, t)
      })
    if (intervalSeconds > limits.maxInterval)
      return t('The interval can be at most {{duration}}.', {
        duration: formatDcaInterval(limits.maxInterval, t)
      })
    return null
  })()

  const executionsError = (() => {
    const min = limits?.minExecutions ?? 2
    if (!executions || !Number.isInteger(executionsNum) || executionsNum < min)
      return t('Enter at least {{count}} executions', { count: min })
    if (limits && executionsNum > limits.maxExecutions)
      return t('Enter at most {{count}} executions', { count: limits.maxExecutions })
    if (limits && !intervalError && intervalSeconds * (executionsNum - 1) > limits.maxDuration)
      return t('The plan cannot run longer than {{duration}}.', {
        duration: formatDcaInterval(limits.maxDuration - (limits.maxDuration % 86400), t)
      })
    return null
  })()

  const networkFeeWei = state?.networkFeeWei ? BigInt(state.networkFeeWei) : null

  // The backend wants every execution worth at least `minExecutionUsd`, so the
  // whole order needs that × executions. In token units when the price is known.
  const minExecutionUsd = Number(config?.minExecutionUsd)
  const minTotalUsd =
    Number.isFinite(minExecutionUsd) && minExecutionUsd > 0 && !executionsError
      ? minExecutionUsd * executionsNum
      : null
  const minAmount =
    minTotalUsd !== null && tokenInUsdPrice
      ? ceilToShownDecimals(minTotalUsd / tokenInUsdPrice)
      : null
  const minLabel =
    !tokenIn || minTotalUsd === null
      ? null
      : minAmount !== null
        ? `${formatDecimals(minAmount, 'amount')} ${tokenIn.symbol}`
        : formatDecimals(minTotalUsd, 'value')

  const amountError = (() => {
    if (!amount) return null
    if (amountRaw === null || amountRaw <= 0n) return t('Enter an amount greater than 0.')
    if (balanceRaw !== null && amountRaw > balanceRaw)
      return t('Insufficient {{symbol}} balance', { symbol: tokenIn?.symbol })
    if (
      minAmount !== null &&
      tokenIn &&
      Number(formatUnits(amountRaw, tokenIn.decimals)) < minAmount
    )
      return t('Enter at least {{min}}. Each execution must be worth {{usd}} or more.', {
        min: minLabel,
        usd: formatDecimals(minExecutionUsd, 'value')
      })
    if (
      balanceRaw !== null &&
      sellsNative &&
      networkFeeWei !== null &&
      amountRaw + networkFeeWei > balanceRaw
    )
      return t('Not enough PLS left to pay the network fee.')
    return null
  })()

  // ---- preview sync ----
  const owner = account?.addr ?? null
  const params = useMemo<DcaOrderParams | null>(() => {
    if (!owner || !tokenIn || !tokenOut || !config) return null
    if (amountRaw === null || amountRaw <= 0n || intervalError || executionsError) return null
    return {
      owner,
      tokenIn: tokenIn.address,
      tokenOut: tokenOut.address,
      totalAmountRaw: amountRaw.toString(),
      totalExecutions: executionsNum,
      interval: intervalSeconds,
      ...(startAt ? { startAt } : {}),
      // Auto: let the backend pick from the route, and show what it picked.
      ...(customSlippageBps !== null ? { maxSlippageBps: slippageBps } : {})
    }
  }, [
    owner,
    tokenIn,
    tokenOut,
    config,
    amountRaw,
    intervalError,
    executionsError,
    executionsNum,
    intervalSeconds,
    startAt,
    customSlippageBps,
    slippageBps
  ])
  const paramsKey = params ? getDcaParamsKey(params) : null
  const previewKey = state?.previewKey ?? null

  useEffect(() => {
    if (!params) {
      if (previewKey) dispatch({ type: 'DCA_CONTROLLER_CLEAR_PREVIEW' })
      return
    }
    if (paramsKey === previewKey) return
    const id = setTimeout(
      () => dispatch({ type: 'DCA_CONTROLLER_PREVIEW', params }),
      PREVIEW_DEBOUNCE_MS
    )
    return () => clearTimeout(id)
    // `params` changes identity with every key change; the key is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, previewKey, dispatch])

  const isPreviewCurrent = !!paramsKey && paramsKey === previewKey
  const preview = isPreviewCurrent && state.previewStatus === 'ready' ? state.previewSummary : null
  const isPreviewLoading = !!params && (!isPreviewCurrent || state.previewStatus === 'loading')
  const previewError =
    isPreviewCurrent && state.previewStatus === 'error' ? state.previewError : null

  // Auto-fill: every preview made in auto mode carries the slippage the backend
  // picked for this route. A value the user typed is never overwritten.
  const previewSlippageBps = preview?.maxSlippageBps ?? null
  useEffect(() => {
    if (customSlippageBps === null && previewSlippageBps !== null)
      setAutoSlippageBps(previewSlippageBps)
  }, [customSlippageBps, previewSlippageBps])

  // ---- route slippage ----
  const requiredSlippageBps = preview?.requiredSlippageBps ?? null
  const suggestedSlippageBps = preview?.suggestedSlippageBps ?? null
  // What the route needs, never shown below the contract floor.
  const routeMinSlippageBps =
    requiredSlippageBps !== null ? Math.max(requiredSlippageBps, minSlippageBps) : minSlippageBps
  // Every buy would be skipped, and `/prepare` refuses the order.
  const isSlippageBelowRouteMin = requiredSlippageBps !== null && slippageBps < requiredSlippageBps
  // A thin pool: the route needs more than 1% just to fill.
  const isThinLiquidity = suggestedSlippageBps !== null && suggestedSlippageBps > 100
  const isMevRisk = slippageBps > 300

  // ---- account gating ----
  // Same rule as getSwapViewOnlyReason: a Safe signs through its owners.
  const isViewOnly =
    !!account && !account.safeCreation && getIsViewOnly(keys, account.associatedKeys)
  const isSmartAccountBlocked = !!account && isSmartAccount(account)
  const hasPulseChain = networks.some((n) => Number(n.chainId) === DCA_CHAIN_ID)
  const blockReason = isViewOnly
    ? t(
        'This is a watch-only account. Import its seed phrase or private key to be able to sign and swap.'
      )
    : isSmartAccountBlocked
      ? t('DCA is available for basic accounts only for now.')
      : !hasPulseChain
        ? t('Add PulseChain to your networks to use DCA.')
        : null

  // Last buy as the form stands, until the preview gives the planned one.
  const estimatedLastExecutionAt =
    !intervalError && !executionsError
      ? new Date(
          (startsNow ? Date.now() : startMs) + intervalSeconds * (executionsNum - 1) * 1000
        ).toISOString()
      : null

  const canReview =
    !!preview &&
    !isSlippageBelowRouteMin &&
    !amountError &&
    !blockReason &&
    state.flowStatus !== 'preparing'

  // ---- setters ----
  const setAmount = useCallback(
    (text: string) => setAmountState(sanitizeAmount(text, tokenIn?.decimals ?? 18)),
    [tokenIn?.decimals]
  )
  const setIntervalValue = useCallback(
    (text: string) => setIntervalValueState(text.replace(/[^0-9]/g, '').slice(0, 6)),
    []
  )
  // The pickers only bound the date, so a time earlier today (or a typed past
  // date) is still selectable. The start can't be in the past: snap it to now.
  const updateStart = useCallback((next: { date: string; time: string }) => {
    const ms = Date.parse(`${next.date}T${next.time}`)
    if (Number.isFinite(ms) && ms < Date.now() - 60_000) {
      const now = new Date()
      setStart({ date: toDateInputValue(now), time: toTimeInputValue(now) })
    } else setStart(next)
  }, [])
  // A start left at "now" goes stale while the form stays open and the end
  // date (counted from the real now) drifts ahead of it. Keep it on the clock.
  useEffect(() => {
    const id = setInterval(() => {
      setStart((prev) => {
        const ms = Date.parse(`${prev.date}T${prev.time}`)
        if (!Number.isFinite(ms) || ms >= Date.now() - 60_000) return prev
        const now = new Date()
        return { date: toDateInputValue(now), time: toTimeInputValue(now) }
      })
    }, 15_000)
    return () => clearInterval(id)
  }, [])
  const setStartDate = useCallback(
    (date: string) => updateStart({ date, time: startTime }),
    [updateStart, startTime]
  )
  const setStartTime = useCallback(
    (time: string) => updateStart({ date: startDate, time }),
    [updateStart, startDate]
  )

  const setExecutions = useCallback(
    (text: string) => setExecutionsState(text.replace(/[^0-9]/g, '').slice(0, 4)),
    []
  )

  const selectTokenIn = useCallback(
    (address: string) => {
      const next = inputTokens.find((tk) => same(tk.address, address))
      if (!next) return
      setTokenInAddr(next.address)
      setAmountState((prev) => sanitizeAmount(prev, next.decimals))
      // Selling PLS to receive WPLS is the same token for the contract.
      const outIsSame =
        same(tokenOutAddr, next.address) ||
        (next.isNative && same(tokenOutAddr, config?.wrappedNativeAddress))
      if (outIsSame) setTokenOutAddr(null)
    },
    [inputTokens, tokenOutAddr, config?.wrappedNativeAddress]
  )

  const selectTokenOut = useCallback(
    (address: string) => {
      setTokenOutAddr(address)
      if (same(address, tokenInAddr)) setTokenInAddr(null)
    },
    [tokenInAddr]
  )

  const canSwitchTokens =
    !!tokenIn &&
    !!tokenOut &&
    inputTokens.some((tk) => same(tk.address, tokenOut.address)) &&
    outputTokens.some((tk) => same(tk.address, tokenIn.address))

  const switchTokens = useCallback(() => {
    if (!canSwitchTokens || !tokenIn || !tokenOut) return
    setTokenInAddr(tokenOut.address)
    setTokenOutAddr(tokenIn.address)
    setAmountState('')
  }, [canSwitchTokens, tokenIn, tokenOut])

  const setMaxAmount = useCallback(() => {
    if (!tokenIn || balanceRaw === null) return
    // Keep twice the estimated gas when selling PLS: the fee is only an estimate.
    const reserve = sellsNative && networkFeeWei !== null ? networkFeeWei * 2n : 0n
    const max = balanceRaw > reserve ? balanceRaw - reserve : 0n
    setAmountState(sanitizeAmount(formatUnits(max, tokenIn.decimals), tokenIn.decimals))
  }, [tokenIn, balanceRaw, sellsNative, networkFeeWei])

  // ---- flow ----
  // Always with the slippage on screen, also when the backend picked it, so
  // the number the user signs is the number the user saw.
  const confirm = useCallback(() => {
    if (!params) return
    dispatch({ type: 'DCA_CONTROLLER_CONFIRM', params: { ...params, maxSlippageBps: slippageBps } })
  }, [dispatch, params, slippageBps])

  const applySuggestedSlippage = useCallback(() => {
    if (suggestedSlippageBps !== null) setCustomSlippageBps(suggestedSlippageBps)
  }, [suggestedSlippageBps])

  const resetFlow = useCallback(() => dispatch({ type: 'DCA_CONTROLLER_RESET' }), [dispatch])

  const flowStatus: DcaFlowStatus = state?.flowStatus ?? 'idle'

  return {
    configStatus,
    config,
    limits,
    inputTokens,
    sendTokens,
    receiveTokens,
    outputTokens,
    tokenIn,
    tokenOut,
    sellsNative,
    selectTokenIn,
    selectTokenOut,
    canSwitchTokens,
    switchTokens,
    amount,
    setAmount,
    amountRaw,
    amountError,
    minLabel,
    balanceRaw,
    setMaxAmount,
    tokenInUsdPrice,
    tokenOutUsdPrice,
    plsUsdPrice,
    intervalValue,
    setIntervalValue,
    intervalUnit,
    setIntervalUnit,
    availableUnits,
    intervalSeconds,
    intervalError,
    executions,
    setExecutions,
    executionsNum,
    executionsError,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    startsNow,
    estimatedLastExecutionAt,
    slippageBps,
    minSlippageBps,
    maxSlippageBps,
    setSlippageBps: setCustomSlippageBps,
    requiredSlippageBps,
    suggestedSlippageBps,
    routeMinSlippageBps,
    isSlippageBelowRouteMin,
    isThinLiquidity,
    isMevRisk,
    applySuggestedSlippage,
    preview,
    previewStepIds: (isPreviewCurrent && state.previewStepIds) || [],
    isPreviewLoading,
    previewError,
    networkFeeWei,
    blockReason,
    canReview,
    confirm,
    resetFlow,
    flowStatus,
    flowSteps: (state?.flowSteps ?? []) as DcaFlowStep[],
    flowSummary: state?.flowSummary ?? null,
    flowError: state?.flowError ?? null,
    createdOrderId: (state?.createdOrderId ?? null) as string | null,
    explorerUrl: networks.find((n) => Number(n.chainId) === DCA_CHAIN_ID)?.explorerUrl
  }
}

export type UseDcaReturn = ReturnType<typeof useDca>

export default useDca
